import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { reviewService } from "../../services/review.service";
import { auth } from "../../config/firebase"; // 🔥 IMPORT FIREBASE AUTH
import "./ReviewSection.css";

const API_BASE_URL = "http://localhost:5172";

const ReviewSection = ({
  productId,
  productName = "Product",
  productImage = "👓",
}) => {
  const [reviews, setReviews] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);

  // Track Current User
  const [currentUser, setCurrentUser] = useState(null);

  // Step 1: Quality Rating, Review Title, Review Content
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewContent, setReviewContent] = useState("");
  const [quality, setQuality] = useState(5);

  // Step 2: Images
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // Lightbox Modal for viewing images
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxReviewIndex, setLightboxReviewIndex] = useState(0);

  const [loading, setLoading] = useState(false);

  // 🔥 Listen for Auth State Changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showModal || showImageLightbox) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showModal, showImageLightbox]);

  const fetchReviews = useCallback(async () => {
    try {
      const data = await reviewService.getReviews(productId);
      const normalized = Array.isArray(data) ? data : data?.reviews ?? [];
      setReviews(normalized);
    } catch (err) {
      console.error(err);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  /* 🔥 CHECK FOR EXISTING USER REVIEW */
  const existingReview = currentUser
    ? reviews.find((r) => r.user_id === currentUser.uid)
    : null;

  /* CALCULATIONS */
  const totalReviews = reviews.length;
  const averageRating =
    totalReviews > 0
      ? (reviews.reduce((acc, r) => acc + r.rating, 0) / totalReviews).toFixed(1)
      : 0;
  const ratingCount = [5, 4, 3, 2, 1].map(
    (star) => reviews.filter((r) => r.rating === star).length,
  );

  const imagesWithReviewIndex = reviews
    .map((r, idx) => ({ image: r.image, reviewIndex: idx }))
    .filter((item) => item.image && item.image.trim())
    .slice(0, 6);

  const allImages = imagesWithReviewIndex.map((item) => item.image);

  /* HELPER: Resolve image URL */
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return "";
    if (imageUrl.startsWith("http")) return imageUrl;
    return `${API_BASE_URL}${
      imageUrl.startsWith("/") ? imageUrl : "/" + imageUrl
    }`;
  };

  /* MODAL LOGIC */

  // 🔥 Pre-fill Modal if user already reviewed
  const openReviewModal = () => {
    if (existingReview) {
      setReviewTitle(existingReview.title || "");
      setReviewContent(existingReview.comment || "");
      setQuality(existingReview.rating || 5);
      
      if (existingReview.image) {
        setImagePreviews([getImageUrl(existingReview.image)]);
      } else {
        setImagePreviews([]);
      }
    } else {
      setReviewTitle("");
      setReviewContent("");
      setQuality(5);
      setImagePreviews([]);
      setUploadedImages([]);
    }
    setShowModal(true);
  };

  const handleModalNext = () => {
    if (!reviewTitle.trim() || !reviewContent.trim()) {
      alert("Please fill in both title and content.");
      return;
    }
    setModalStep(2);
  };

  const handleModalBack = () => {
    setModalStep(1);
  };

  const resetModal = () => {
    setReviewTitle("");
    setReviewContent("");
    setQuality(5);
    setUploadedImages([]);
    setImagePreviews([]);
    setModalStep(1);
    setShowModal(false);
  };

  /* IMAGE HANDLING */
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const file = files[0];
    if (file) {
      setUploadedImages([file]);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreviews([event.target.result]);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setUploadedImages([]);
    setImagePreviews([]);
  };

  /* LIGHTBOX HANDLERS */
  const openImageLightbox = (images, startIndex = 0, reviewIndex = 0) => {
    setLightboxImages(images);
    setCurrentImageIndex(startIndex);
    setLightboxReviewIndex(reviewIndex);
    setShowImageLightbox(true);
  };

  const closeLightbox = () => {
    setShowImageLightbox(false);
  };

  const nextImage = () => {
    const newIndex = (currentImageIndex + 1) % lightboxImages.length;
    setCurrentImageIndex(newIndex);
    if (newIndex < imagesWithReviewIndex.length) {
      setLightboxReviewIndex(imagesWithReviewIndex[newIndex]?.reviewIndex || 0);
    }
  };

  const prevImage = () => {
    const newIndex =
      (currentImageIndex - 1 + lightboxImages.length) % lightboxImages.length;
    setCurrentImageIndex(newIndex);
    if (newIndex < imagesWithReviewIndex.length) {
      setLightboxReviewIndex(imagesWithReviewIndex[newIndex]?.reviewIndex || 0);
    }
  };

  /* SUBMIT REVIEW */
  const handleSubmit = async () => {
    if (!reviewContent.trim()) return;

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("rating", quality);
      formData.append("title", reviewTitle);
      formData.append("comment", reviewContent);

      if (uploadedImages.length > 0) {
        formData.append("image", uploadedImages[0]);
      }

      await reviewService.addReview(productId, formData);
      resetModal();
      fetchReviews();
    } catch (err) {
      console.error(err);
      alert("Failed to save review. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Recently";
    const date = new Date(dateString);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  return (
    <div className="review-wrapper">
      <h2 className="reviews-main-heading">Customer Reviews</h2>

      <div className="review-top-section">
        <div className="rating-summary">
          <div className="rating-large">{averageRating}</div>
          <div className="stars-large">
            {[...Array(Math.round(averageRating))].map((_, i) => (
              <span key={i} className="star">★</span>
            ))}
          </div>
          <p className="total-reviews">{totalReviews} reviews</p>

          <div className="rating-bars">
            {[5, 4, 3, 2, 1].map((star, i) => (
              <div key={star} className="rating-row">
                <span className="star-label">{star}★</span>
                <div className="bar">
                  <div
                    className="fill"
                    style={{
                      width:
                        totalReviews === 0
                          ? "0%"
                          : `${(ratingCount[i] / totalReviews) * 100}%`,
                    }}
                  ></div>
                </div>
                <span className="count">{ratingCount[i]}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="customer-photos">
          <h4>Customer Photos</h4>
          <div className="photo-strip">
            {allImages.length > 0 ? (
              <>
                {imagesWithReviewIndex.slice(0, 5).map((item, i) => (
                  <img
                    key={i}
                    src={getImageUrl(item.image)}
                    alt={`customer-${i}`}
                    onClick={() =>
                      openImageLightbox(allImages, i, item.reviewIndex)
                    }
                    style={{ cursor: "pointer" }}
                  />
                ))}
                {allImages.length > 5 && (
                  <div
                    className="photo-badge"
                    onClick={() =>
                      openImageLightbox(
                        allImages,
                        5,
                        imagesWithReviewIndex[5]?.reviewIndex || 0,
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    +{allImages.length - 5}
                  </div>
                )}
              </>
            ) : (
              <p className="no-photos">No customer photos yet</p>
            )}
          </div>
        </div>

        <div className="write-review-box">
          {/* 🔥 DYNAMIC BUTTON TEXT */}
          <button
            className="write-review-btn"
            onClick={openReviewModal}
          >
            {existingReview ? "Edit your review" : "Write a review"}
          </button>
        </div>
      </div>

      <div className="reviews-controls">
        <span className="showing-text">
          Showing 1 - {Math.min(10, totalReviews)} of {totalReviews} reviews
        </span>
        <div className="sort-controls">
          <select defaultValue="all" className="sort-dropdown">
            <option value="all">All({totalReviews})</option>
            <option value="5">5 Stars ({ratingCount[0]})</option>
            <option value="4">4 Stars ({ratingCount[1]})</option>
            <option value="3">3 Stars ({ratingCount[2]})</option>
            <option value="2">2 Stars ({ratingCount[3]})</option>
            <option value="1">1 Star ({ratingCount[4]})</option>
          </select>
          <select defaultValue="latest" className="sort-dropdown">
            <option value="latest">Latest</option>
            <option value="oldest">Oldest</option>
            <option value="helpful">Most Helpful</option>
          </select>
        </div>
      </div>

      <div className="review-list">
        {reviews.length > 0 ? (
          reviews.slice(0, 10).map((r, index) => (
            <div key={index} className="review-card">
              <div className="review-card-top">
                <div className="reviewer-info">
                  <div className="reviewer-avatar">
                    {r.name?.charAt(0) || "U"}
                  </div>
                  <div className="reviewer-details">
                    <strong className="reviewer-name">
                      {r.name || "Anonymous"}
                    </strong>
                    <span className="reviewer-country">🇮🇳</span>
                    <span className="review-date">
                      {formatDate(r.createdAt || r.date)}
                    </span>
                  </div>
                </div>
                <div className="review-stars-rating">
                  {[...Array(r.rating || 5)].map((_, i) => (
                    <span key={i} className="star-icon">★</span>
                  ))}
                </div>
              </div>

              {r.title && <h5 className="review-title">{r.title}</h5>}
              <p className="review-comment">{r.comment}</p>

              {r.image && r.image.trim() && (
                <div className="review-images">
                  <img src={getImageUrl(r.image)} alt={`review-${index}`} />
                </div>
              )}

              <div className="review-footer">
                <span className="helpful-text">Is this helpful?</span>
                <div className="helpful-buttons">
                  <button className="helpful-btn thumbs-up">
                    👍 <span>{r.helpful || 0}</span>
                  </button>
                  <button className="helpful-btn thumbs-down">
                    👎 <span>{r.unhelpful || 0}</span>
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-reviews">
            No reviews yet. Be the first to review!
          </div>
        )}
      </div>

      {showModal &&
        modalStep === 1 &&
        createPortal(
          <div className="review-modal-overlay">
            <div className="review-modal">
              <div className="modal-header">
                {/* 🔥 DYNAMIC MODAL HEADER */}
                <h3>{existingReview ? "Update your review" : "Write a review"}</h3>
                <button className="close-btn" onClick={resetModal}>✕</button>
              </div>

              <div className="modal-product-info">
                {typeof productImage === "string" &&
                productImage.startsWith("http") ? (
                  <img
                    src={productImage}
                    alt="product"
                    className="product-image"
                  />
                ) : (
                  <span className="product-icon">{productImage}</span>
                )}
                <span className="product-name">{productName}</span>
              </div>

              <div className="modal-section">
                <label>Quality*</label>
                <div className="star-rating-selector">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className={`star-btn ${star <= quality ? "active" : ""}`}
                      onClick={() => setQuality(star)}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              <div className="modal-section">
                <label htmlFor="review-title">Review Title</label>
                <input
                  id="review-title"
                  type="text"
                  placeholder="Review Title"
                  value={reviewTitle}
                  onChange={(e) => setReviewTitle(e.target.value)}
                  className="modal-input"
                />
              </div>

              <div className="modal-section">
                <label htmlFor="review-content">Review Content</label>
                <textarea
                  id="review-content"
                  placeholder="Review Content"
                  value={reviewContent}
                  onChange={(e) => setReviewContent(e.target.value)}
                  className="modal-textarea"
                  rows="6"
                />
              </div>

              <div className="modal-actions">
                <button className="modal-btn cancel-btn" onClick={resetModal}>
                  Cancel
                </button>
                <button
                  className="modal-btn next-btn"
                  onClick={handleModalNext}
                >
                  Next
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showModal &&
        modalStep === 2 &&
        createPortal(
          <div className="review-modal-overlay">
            <div className="review-modal">
              <div className="modal-header">
                <h3>Add Photos (Optional)</h3>
                <button className="close-btn" onClick={resetModal}>✕</button>
              </div>

              <div className="modal-section">
                <label htmlFor="image-upload">Upload Images</label>
                <input
                  id="image-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="file-input"
                />
              </div>

              {imagePreviews.length > 0 && (
                <div className="image-preview-grid">
                  {imagePreviews.map((preview, i) => (
                    <div key={i} className="preview-item">
                      <img src={preview} alt={`preview-${i}`} />
                      <button
                        className="remove-btn"
                        onClick={() => removeImage()}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-actions">
                <button
                  className="modal-btn back-btn"
                  onClick={handleModalBack}
                >
                  Back
                </button>
                <button
                  className="modal-btn submit-btn"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {/* 🔥 DYNAMIC SUBMIT BUTTON */}
                  {loading ? "Saving..." : existingReview ? "Update" : "Submit"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* LIGHTBOX CODE REMAINS THE SAME (Omitted for brevity, paste your existing lightbox code here) */}
      {showImageLightbox &&
        lightboxImages.length > 0 &&
        createPortal(
          <div className="image-lightbox-overlay">
            <div className="image-lightbox-split-container">
              <div className="lightbox-left-section">
                <button className="lightbox-close-btn" onClick={closeLightbox}>
                  ✕
                </button>
                <div className="lightbox-image-wrapper">
                  <img
                    src={getImageUrl(lightboxImages[currentImageIndex])}
                    alt={`lightbox-${currentImageIndex}`}
                    className="lightbox-image"
                  />
                </div>
                <button
                  className="lightbox-arrow lightbox-arrow-left"
                  onClick={prevImage}
                  disabled={lightboxImages.length === 1}
                >
                  ‹
                </button>
                <button
                  className="lightbox-arrow lightbox-arrow-right"
                  onClick={nextImage}
                  disabled={lightboxImages.length === 1}
                >
                  ›
                </button>
                {lightboxImages.length > 1 && (
                  <div className="lightbox-counter">
                    {currentImageIndex + 1} / {lightboxImages.length}
                  </div>
                )}
              </div>

              <div className="lightbox-right-section">
                {reviews.length > 0 && reviews[lightboxReviewIndex] && (
                  <div className="lightbox-review-details">
                    <div className="lightbox-reviewer-header">
                      <div className="lightbox-avatar">
                        {reviews[lightboxReviewIndex].name?.charAt(0) || "U"}
                      </div>
                      <div>
                        <h4 className="lightbox-reviewer-name">
                          {reviews[lightboxReviewIndex].name || "Anonymous"}
                        </h4>
                        <span className="lightbox-date">
                          {formatDate(
                            reviews[lightboxReviewIndex].createdAt ||
                              reviews[lightboxReviewIndex].date,
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="lightbox-rating">
                      {[...Array(reviews[lightboxReviewIndex].rating || 5)].map(
                        (_, i) => (
                          <span key={i} className="lightbox-star">★</span>
                        ),
                      )}
                    </div>
                    {reviews[lightboxReviewIndex].title && (
                      <h5 className="lightbox-review-title">
                        {reviews[lightboxReviewIndex].title}
                      </h5>
                    )}
                    <p className="lightbox-review-comment">
                      {reviews[lightboxReviewIndex].comment}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ReviewSection;